/**
 * ChatPinnedBanner — TG-style banner закреплённых сообщений в шапке ленты.
 *
 * Извлечён из ChatPage.tsx (P2#10a). Компонент чисто-презентационный:
 * принимает list + currentIdx, в parent поддерживает state pinnedIdx и
 * pinnedListOpen. Это сознательно — banner может появляться/исчезать
 * вместе с lifecycle ChatPage, но за состояние «какой закреп показан»
 * и «открыт ли список» отвечает родитель (там же live data из pinnedQ).
 *
 * Поведение:
 *  - Клик по banner → jumpToMessage(pinned[idx].id) + цикл idx если их >1.
 *  - Клик по ☰-кнопке справа → открыть полный список (modal/panel).
 */
import type { ChatMessage } from "@/api/dashboard";

export type ChatPinnedBannerProps = {
  pinned: ChatMessage[];
  pinnedIdx: number;
  onCycle: (next: number) => void;
  onJump: (messageId: number) => void;
  onOpenList: () => void;
};

export function ChatPinnedBanner({
  pinned, pinnedIdx, onJump, onOpenList,
}: ChatPinnedBannerProps) {
  if (pinned.length === 0) return null;
  const idx = Math.min(pinnedIdx, pinned.length - 1);
  const current = pinned[idx];
  if (!current) return null;

  return (
    <div
      className="chat-pinned-banner tg-pinned-v2"
      onClick={() => {
        // Только прыжок. pinnedIdx обновляется автоматически из родителя
        // по scroll-позиции (TG-стиль).
        onJump(current.id);
      }}
      title={pinned.length > 1
        ? `Перейти (${idx + 1} / ${pinned.length}) — клик переключает`
        : "Перейти к закреплённому"}
    >
      <div className="tg-pinned-bar" />
      <div className="tg-pinned-body">
        <div className="tg-pinned-title">
          Закреплённое сообщение{pinned.length > 1 ? ` #${idx + 1}` : ""}
        </div>
        <div className="tg-pinned-text">
          {current.text || (current.attachments?.length ? "файл" : "—")}
        </div>
      </div>
      <button
        type="button"
        className="tg-pinned-action"
        title={`Все закреплённые (${pinned.length})`}
        onClick={(e) => { e.stopPropagation(); onOpenList(); }}
        aria-label="Список закреплённых"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
      </button>
    </div>
  );
}
